import type { ComparisonPage } from '../types';

export const supermetricsVsWarehouse: ComparisonPage = {
  slug: 'supermetrics-vs-custom-data-warehouse',
  group: 'ecomm',
  ours: 'greyfield',
  theirs: 'supermetrics',
  title: 'Supermetrics vs a custom data warehouse: a data pipe or a finished model?',
  metaTitle: 'Supermetrics vs a Custom Ecommerce Data Warehouse (2026): Honest Comparison',
  description:
    'Supermetrics moves marketing and store data into Sheets, Looker Studio, or a warehouse. It does not model profit. Here is how it fits with a custom build.',
  keywords: ['supermetrics alternative', 'supermetrics vs', 'supermetrics pricing', 'supermetrics snowflake', 'supermetrics shopify'],
  published: '2026-11-03',
  updated: '2026-11-03',
  tldr:
    'Supermetrics and a custom warehouse are not really alternatives. Supermetrics moves data; a warehouse build models it. For marketing reports in Sheets or Looker Studio, Supermetrics alone is often enough and costs far less. When you need profit, refunds, and orders matched across channels, Supermetrics can be the ingestion layer and the modeling still has to happen somewhere.',
  differences: [
    { heading: 'Moving data vs modeling it', body: 'Supermetrics delivers data from hundreds of sources as standard tables per source. Matching orders across channels and calculating profit is left to you.' },
    { heading: 'Sheets by default, warehouses on Enterprise', body: 'Self-serve plans deliver to Sheets, Excel, Looker Studio, Power BI, and AI chat. Snowflake, BigQuery, and its own Storage need the custom-priced Enterprise plan.' },
    { heading: 'It can be part of a build', body: 'On Enterprise, Supermetrics can land data in your Snowflake. A custom build can use it, or another connector, to feed the models.' },
  ],
  sections: [
    {
      heading: 'What Supermetrics does well',
      paragraphs: [
        'It maintains hundreds of connectors for ad platforms, analytics, email, CRM, and ecommerce, including Shopify, Amazon Seller Central, Recharge, and TikTok Shop in early access. API pipelines are usually the most fragile part of a custom stack, and Supermetrics takes that off your hands.',
        'Entry plans are self-serve with published prices, and it has added natural-language dashboards, an MCP server, and integrations with ChatGPT, Claude, Gemini, and Copilot.',
      ],
    },
    {
      heading: 'What still needs modeling',
      paragraphs: [
        'Its own docs list the gaps. The Shopify connector uses the product’s current unit cost rather than the cost at the time of sale, and treats a missing cost as 100% margin. Refunds are dated when processed, canceled refunds are not reported, and edited orders can create duplicates. The Amazon field list covers orders, sales, and refunds but no fee or settlement fields.',
        'Those are exactly the problems a warehouse model exists to solve: [refund date vs order date](/blog/shopify-refund-date-vs-order-date), [Amazon fees that arrive after the sale](/blog/why-amazon-profit-changes-after-sale), and [orders counted twice across channels](/blog/avoid-double-counting-shopify-amazon-tiktok-orders).',
      ],
    },
  ],
  pickThem: [
    'Your marketing team wants ad and store data in Sheets or Looker Studio this week.',
    'Your questions are about spend, ROAS, and campaign performance, not profit.',
    'You want a managed feed into a warehouse you already run.',
  ],
  pickUs: [
    'You need contribution margin with dated costs, fees, and refunds.',
    'Orders must be matched across Shopify, Amazon, and TikTok Shop.',
    'You want the business rules written, tested, and owned in dbt.',
  ],
  byTeam: [
    { team: 'Marketing team reporting in Looker Studio', pick: 'theirs', why: 'Published self-serve plans and no engineering needed.' },
    { team: 'Agency reporting across many client ad accounts', pick: 'theirs', why: 'Connector coverage and account limits built for that.' },
    { team: 'Finance team that needs profit by channel', pick: 'ours', why: 'COGS, fees, and refunds modeled correctly over time.' },
    { team: 'Brand already paying for Supermetrics Enterprise', pick: 'both', why: 'Use it to load Snowflake and build the models on top.' },
  ],
  faq: [
    {
      question: 'Does a custom warehouse replace Supermetrics?',
      answer: 'It can, by using a different connector tool, but it does not have to. If you already use Supermetrics Enterprise, it can feed the warehouse, and the build focuses on modeling.',
    },
    {
      question: 'Can Supermetrics send Shopify data to Snowflake?',
      answer: 'Yes, on the Enterprise plan, which is quoted by sales. Self-serve plans deliver to spreadsheets, Looker Studio, Power BI, and AI chat tools.',
    },
    {
      question: 'Does Supermetrics calculate profit?',
      answer: 'Not as a modeled metric. Its Shopify connector exposes cost fields, but its documentation notes it uses the current unit cost, so historical margin needs modeling downstream.',
    },
  ],
  related: ['improvado-vs-custom-data-warehouse', 'polar-analytics-vs-custom-data-warehouse', 'triple-whale-vs-custom-data-warehouse'],
};
